import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError, API as API_BASE } from "../lib/api";
import {
    Plus,
    Eye,
    FileVideo,
    Sparkles,
    DollarSign,
    Building2,
    Grid3x3,
    List,
} from "lucide-react";
import { toast } from "sonner";
import CreatorPerformance from "../components/CreatorPerformance";
import PayoutForm from "../components/PayoutForm";
import CreatorBrandsPanel from "../components/CreatorBrandsPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const CHANNEL_LABELS = {
    meta: "Meta",
    google: "Google",
    amazon: "Amazon",
    youtube: "YouTube",
    pinterest: "Pinterest",
    snap: "Snap",
};

const BACKEND = API_BASE.replace(/\/api$/, "");

function VideoThumb({ v }) {
    if (v.thumbnail_url) {
        return (
            <img
                src={v.thumbnail_url}
                alt={v.title}
                className="w-full h-full object-cover"
            />
        );
    }
    if (v.thumbnail_path) {
        return (
            <img
                src={`${BACKEND}/api/videos/${v.id}/thumbnail`}
                alt={v.title}
                className="w-full h-full object-cover"
            />
        );
    }
    return (
        <div className="w-full h-full flex items-center justify-center text-slate-300">
            <FileVideo className="w-12 h-12" />
        </div>
    );
}

export default function CreatorDashboard() {
    const { user } = useAuth();
    const [videos, setVideos] = React.useState([]);
    const [stats, setStats] = React.useState({ videos: 0, licenses: 0, approved: 0, views: 0 });
    const [earnings, setEarnings] = React.useState({ items: [], total_paid: 0 });
    const [brands, setBrands] = React.useState([]);
    const [pendingChangesCount, setPendingChangesCount] = React.useState(0);

    const [view, setView] = React.useState(() => {
        try {
            return localStorage.getItem("castline_creator_videos_view") || "grid";
        } catch (_e) {
            return "grid";
        }
    });
    const [brandFilter, setBrandFilter] = React.useState("all");
    const [channelFilter, setChannelFilter] = React.useState("all");

    const setViewMode = (m) => {
        setView(m);
        try {
            localStorage.setItem("castline_creator_videos_view", m);
        } catch (_e) {
            /* noop */
        }
    };

    const load = React.useCallback(async () => {
        try {
            const [v, s, e, b, ch] = await Promise.all([
                api.get("/creator/videos"),
                api.get("/creator/stats"),
                api.get("/creator/earnings"),
                api.get("/creator/brands"),
                api.get("/creator/payout-changes"),
            ]);
            setVideos(v.data);
            setStats(s.data);
            setEarnings(e.data);
            setBrands(b.data);
            setPendingChangesCount(ch.data.length);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    // Channels pool derived from all videos
    const channelsInUse = React.useMemo(() => {
        const set = new Set();
        for (const v of videos) (v.channels || []).forEach((c) => set.add(c));
        return Array.from(set);
    }, [videos]);

    const filteredVideos = React.useMemo(() => {
        return videos.filter((v) => {
            if (channelFilter !== "all" && !(v.channels || []).includes(channelFilter)) {
                return false;
            }
            // "Brand filter" = video is usable by that brand (the creator is connected to it).
            // Since every video is available to every connected brand in the invite-only
            // model, this simply checks the creator↔brand link exists. Keep the dropdown
            // for explicit discovery of per-brand views during the pitch.
            if (brandFilter !== "all") {
                const hit = brands.some((b) => b.id === brandFilter);
                if (!hit) return false;
            }
            return true;
        });
    }, [videos, brandFilter, channelFilter, brands]);

    const deleteVideo = async (id) => {
        try {
            await api.delete(`/videos/${id}`);
            toast.success("Video removed");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="min-h-screen bg-white text-slate-900" data-testid="creator-dashboard">
            <Navbar />

            <section className="border-b hairline surface-warm">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-10 flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="chip chip-lime inline-flex mb-3">CREATOR</div>
                        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                            Hey, {user?.name?.split(" ")[0]} 👋
                        </h1>
                        <p className="text-slate-500 mt-2 max-w-xl">
                            Upload videos. Get paid when brands run them.
                        </p>
                    </div>
                    <Link to="/creator/upload">
                        <button className="btn-pill-primary inline-flex items-center" data-testid="upload-cta">
                            <Plus className="w-4 h-4 mr-2" /> Upload video
                        </button>
                    </Link>
                </div>
            </section>

            <section
                className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-4"
                data-testid="stats"
            >
                {[
                    ["Videos", stats.videos, FileVideo, "lime"],
                    ["Views", stats.views, Eye, "coral"],
                    ["Earnings (paid)", `$${(earnings.total_paid || 0).toLocaleString()}`, DollarSign, "coral"],
                    ["Brands", brands.length, Building2, "lime"],
                ].map(([label, value, Icon, accent]) => (
                    <div key={label} className="card-ecom p-5" data-testid={`stat-${label}`}>
                        <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${accent === "coral" ? "bg-coral/10" : "bg-lime/30"}`}
                        >
                            <Icon className={`w-4 h-4 ${accent === "coral" ? "text-coral" : "text-lime-ink"}`} />
                        </div>
                        <div className="text-xs text-slate-500">{label}</div>
                        <div className="font-display text-3xl font-semibold mt-0.5">{value}</div>
                    </div>
                ))}
            </section>

            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-6" data-testid="creator-tabs-section">
                <Tabs defaultValue={pendingChangesCount > 0 ? "brands" : "performance"}>
                    <TabsList className="rounded-full bg-slate-100 p-1 h-auto" data-testid="creator-tabs">
                        {[
                            ["performance", "Performance"],
                            [
                                "brands",
                                pendingChangesCount > 0
                                    ? `Brands · ${pendingChangesCount} action required`
                                    : "Brands",
                            ],
                            ["payouts", "Payment method"],
                        ].map(([v, l]) => (
                            <TabsTrigger
                                key={v}
                                value={v}
                                className="rounded-full data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-5 py-2"
                                data-testid={`creator-tab-${v}`}
                            >
                                {l}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="performance" className="mt-6">
                        <CreatorPerformance />
                    </TabsContent>

                    <TabsContent value="brands" className="mt-6">
                        <CreatorBrandsPanel />
                    </TabsContent>

                    <TabsContent value="payouts" className="mt-6">
                        <PayoutForm />
                    </TabsContent>
                </Tabs>
            </section>

            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-6">
                <div
                    className="flex items-center justify-between flex-wrap gap-3 mb-5"
                    data-testid="my-videos-toolbar"
                >
                    <div className="overline text-slate-500">MY VIDEOS</div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <select
                            value={brandFilter}
                            onChange={(e) => setBrandFilter(e.target.value)}
                            className="rounded-full bg-slate-100 border-0 text-sm px-4 py-2 pr-9 focus:ring-2 focus:ring-coral focus:outline-none"
                            data-testid="brand-filter"
                        >
                            <option value="all">All brands · {brands.length}</option>
                            {brands.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}
                                </option>
                            ))}
                        </select>
                        <select
                            value={channelFilter}
                            onChange={(e) => setChannelFilter(e.target.value)}
                            className="rounded-full bg-slate-100 border-0 text-sm px-4 py-2 pr-9 focus:ring-2 focus:ring-coral focus:outline-none"
                            data-testid="channel-filter"
                        >
                            <option value="all">All channels</option>
                            {channelsInUse.map((c) => (
                                <option key={c} value={c}>
                                    {CHANNEL_LABELS[c] || c}
                                </option>
                            ))}
                        </select>
                        <div
                            className="flex items-center gap-1 bg-slate-100 rounded-full p-1"
                            data-testid="creator-view-toggle"
                        >
                            <button
                                onClick={() => setViewMode("grid")}
                                className={`p-1.5 rounded-full transition-all ${
                                    view === "grid"
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-500"
                                }`}
                                title="Grid"
                                data-testid="creator-view-grid"
                            >
                                <Grid3x3 className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewMode("list")}
                                className={`p-1.5 rounded-full transition-all ${
                                    view === "list"
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-500"
                                }`}
                                title="List"
                                data-testid="creator-view-list"
                            >
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {videos.length === 0 ? (
                    <div className="card-ecom p-12 text-center" data-testid="no-videos">
                        <Sparkles className="w-8 h-8 text-coral mx-auto mb-3" />
                        <p className="text-slate-600 mb-4">No videos yet.</p>
                        <Link to="/creator/upload">
                            <button className="btn-pill-primary">Upload your first video</button>
                        </Link>
                    </div>
                ) : filteredVideos.length === 0 ? (
                    <div
                        className="card-ecom p-10 text-center text-sm text-slate-500"
                        data-testid="no-videos-filter"
                    >
                        No videos match this filter.
                    </div>
                ) : view === "grid" ? (
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5"
                        data-testid="my-videos-grid"
                    >
                        {filteredVideos.map((v) => (
                            <div
                                key={v.id}
                                className="card-ecom overflow-hidden"
                                data-testid={`my-video-${v.id}`}
                            >
                                <div className="aspect-[4/5] bg-slate-100 relative overflow-hidden">
                                    <VideoThumb v={v} />
                                    {v.channels && v.channels.length > 0 && (
                                        <div className="absolute top-2 left-2 flex gap-1 flex-wrap max-w-[calc(100%-16px)]">
                                            {v.channels.slice(0, 3).map((c) => (
                                                <span
                                                    key={c}
                                                    className="chip chip-lime text-[10px]"
                                                >
                                                    {CHANNEL_LABELS[c] || c}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="p-4">
                                    <div className="font-display text-base font-semibold leading-tight line-clamp-2">
                                        {v.title}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
                                        <span>{v.category}</span>
                                        <span>{v.views || 0} views</span>
                                    </div>
                                    <button
                                        onClick={() => deleteVideo(v.id)}
                                        className="mt-3 text-xs text-red-500 hover:text-red-600"
                                        data-testid={`delete-${v.id}`}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div
                        className="card-ecom divide-y hairline overflow-hidden"
                        data-testid="my-videos-list"
                    >
                        {filteredVideos.map((v) => (
                            <div
                                key={v.id}
                                className="p-4 flex items-center gap-4 hover:bg-slate-50/60 transition-colors"
                                data-testid={`my-video-row-${v.id}`}
                            >
                                <div className="w-28 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                                    <VideoThumb v={v} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold truncate">
                                        {v.title}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                        <span>{v.category}</span>
                                        {v.duration && <span>· {v.duration}s</span>}
                                        <span>· {v.views || 0} views</span>
                                        {v.channels && v.channels.length > 0 && (
                                            <span className="flex gap-1 ml-1">
                                                {v.channels.map((c) => (
                                                    <span
                                                        key={c}
                                                        className="chip chip-lime text-[10px]"
                                                    >
                                                        {CHANNEL_LABELS[c] || c}
                                                    </span>
                                                ))}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => deleteVideo(v.id)}
                                    className="text-xs text-red-500 hover:text-red-600"
                                    data-testid={`delete-list-${v.id}`}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
